import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { DeleteFunctionPayload } from '@polyai/stream/commands/handlers/functions/delete-function/schema';
import type {
  EndFunctionCreatePayload,
  StartFunctionCreatePayload,
} from '@polyai/stream/commands/handlers/special-functions/create-function/schema';
import type {
  EndFunctionUpdatePayload,
  StartFunctionUpdatePayload,
} from '@polyai/stream/commands/handlers/special-functions/update-function/schema';
import { updateVariable } from '@polyai/stream/store/reducers/variables';

import { assignMetadata } from '../../utils/assign-metadata';
import { replaceVariableNameInCode } from '../../utils/functions-helpers';
import {
  createEndFunction,
  createStartFunction,
  deleteEndFunction,
  deleteStartFunction,
  updateEndFunction,
  updateStartFunction,
} from './actions';
import type { SpecialFunction, SpecialFunctionsState } from './types';
import { createSpecialFunctionWithEntityStates } from './utils';

const initialState: SpecialFunctionsState = {
  startFunction: undefined,
  endFunction: undefined,
};

const slice = createSlice({
  name: 'specialFunctions',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(
      createStartFunction,
      (state, action: PayloadAction<StartFunctionCreatePayload>) => {
        const newFunction = createSpecialFunctionWithEntityStates(
          action,
          action.payload,
        );
        state.startFunction = newFunction;
      },
    );

    builder.addCase(
      updateStartFunction,
      (state, action: PayloadAction<StartFunctionUpdatePayload>) => {
        const functionData = state.startFunction;
        const payload = action.payload;
        if (functionData) {
          const newStartFunction: SpecialFunction = {
            id: functionData.id,
            name: functionData.name,
            description: functionData.description,
            code: payload.code ?? functionData.code,
            parameters: functionData.parameters,
            errors: payload.errors?.errors ?? functionData.errors,
            archived: !!payload.archived,
            references: payload.references ?? functionData.references,
            createdAt: '',
            createdBy: '',
            updatedAt: '',
            updatedBy: '',
          };
          assignMetadata(action, newStartFunction);
          state.startFunction = newStartFunction;
        }
      },
    );

    builder.addCase(
      updateEndFunction,
      (state, action: PayloadAction<EndFunctionUpdatePayload>) => {
        const functionData = state.endFunction;
        const payload = action.payload;
        if (functionData) {
          const newEndFunction: SpecialFunction = {
            id: functionData.id,
            name: functionData.name,
            description: functionData.description,
            code: payload.code ?? functionData.code,
            parameters: functionData.parameters,
            errors: payload.errors?.errors ?? functionData.errors,
            archived: !!payload.archived,
            references: payload.references ?? functionData.references,
            createdAt: '',
            createdBy: '',
            updatedAt: '',
            updatedBy: '',
          };
          assignMetadata(action, newEndFunction);
          state.endFunction = newEndFunction;
        }
      },
    );

    builder.addCase(
      deleteStartFunction,
      (state, action: PayloadAction<DeleteFunctionPayload>) => {
        if (action.payload.id === state.startFunction?.id) {
          state.startFunction = undefined;
        }
      },
    );

    builder.addCase(
      deleteEndFunction,
      (state, action: PayloadAction<DeleteFunctionPayload>) => {
        if (action.payload.id === state.endFunction?.id) {
          state.endFunction = undefined;
        }
      },
    );

    // When a variable is renamed, update code references
    builder.addCase(updateVariable, (state, action) => {
      const { id: variableId, name: newName, oldName } = action.payload;

      if (!newName || !oldName || newName === oldName) {
        return;
      }

      // start function
      const sf = state.startFunction;
      if (sf && sf.references?.variables?.[variableId]) {
        state.startFunction = {
          ...sf,
          code: replaceVariableNameInCode(sf.code, oldName, newName),
        };
      }

      // end function
      const ef = state.endFunction;
      if (ef && ef.references?.variables?.[variableId]) {
        state.endFunction = {
          ...ef,
          code: replaceVariableNameInCode(ef.code, oldName, newName),
        };
      }
    });

    builder.addCase(
      createEndFunction,
      (state, action: PayloadAction<EndFunctionCreatePayload>) => {
        const newFunction = createSpecialFunctionWithEntityStates(
          action,
          action.payload,
        );
        state.endFunction = newFunction;
      },
    );
  },
});

export {
  createEndFunction,
  createStartFunction,
  deleteEndFunction,
  deleteStartFunction,
  updateEndFunction,
  updateStartFunction,
};

export { specialFunctionsReferencesAdapter } from './utils';
export type { SpecialFunction, SpecialFunctionsState };

export default slice.reducer;
